<template>
  <div
    class="measure-result-panel"
    :style="panelThemeCssVars"
  >
    <div
      v-if="!hasContent"
      class="measure-result-panel__empty"
    >
      <n-text depth="3">请选择要测量的几何</n-text>
    </div>

    <template v-else>
      <!-- 双选结果：hover 预览线，点选钉住线与选中态 -->
      <div
        v-if="pairCallouts.length > 0"
        class="measure-result-panel__section"
      >
        <div class="measure-result-panel__section-title">
          <n-text depth="3">双选测量</n-text>
        </div>
        <button
          v-for="item in pairCallouts"
          :key="item.calloutId"
          type="button"
          class="measure-result-row measure-result-row--selectable"
          :class="{ 'measure-result-row--active': isItemActive(item.kind) }"
          :aria-pressed="isItemActive(item.kind)"
          @click="emit('select-item', item)"
          @pointerenter="emit('hover-item', item)"
          @pointerleave="emit('hover-leave')"
        >
          <n-text
            depth="3"
            class="measure-result-row__label"
          >
            {{ item.label }}
          </n-text>
          <n-text class="measure-result-row__value">{{ item.value }}</n-text>
        </button>
      </div>

      <!-- 单选属性只读 -->
      <div
        v-if="singleViewState"
        class="measure-result-panel__section"
      >
        <div class="measure-result-panel__section-title">
          <n-text class="measure-result-panel__entity-title">
            {{ singleTitle }}
          </n-text>
        </div>
        <div
          v-for="(row, index) in singleViewState.rows"
          :key="`single-row-${index}-${row.label}`"
          class="measure-result-row"
        >
          <n-text
            depth="3"
            class="measure-result-row__label"
          >
            {{ row.label }}
          </n-text>
          <n-text class="measure-result-row__value">{{ row.value }}</n-text>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { NText, useThemeVars } from 'naive-ui';
import {
  MeasureLineVisibilityGroup,
  MeasurePairMeasurementCalloutKind,
  type MeasureCalloutViewState,
  type MeasurePairMeasurementCalloutItem,
} from '@puppycad/cad-core';
import { isMeasureResultItemActive } from '@/composables/measure/useMeasureResultPanel';

/**
 * 测量结果面板属性。
 */
interface MeasureResultPanelProps {
  /** 单选属性视图状态。 */
  singleViewState: MeasureCalloutViewState | null;
  /** 双选结果项列表。 */
  pairCallouts: readonly MeasurePairMeasurementCalloutItem[];
  /** 当前测量线可视化选中组。 */
  lineVisibilityGroup: MeasureLineVisibilityGroup;
}

const props = withDefaults(defineProps<MeasureResultPanelProps>(), {
  singleViewState: null,
  pairCallouts: () => [],
  lineVisibilityGroup: MeasureLineVisibilityGroup.None,
});

const emit = defineEmits<{
  /** 点选双选结果项。 */
  'select-item': [item: MeasurePairMeasurementCalloutItem];
  /** 指针进入双选结果项（hover 预览）。 */
  'hover-item': [item: MeasurePairMeasurementCalloutItem];
  /** 指针离开双选结果项。 */
  'hover-leave': [];
}>();

/**
 * Naive UI 主题变量。
 *
 * 设计说明：与 FloatingPanel 一致，通过 useThemeVars 写入 CSS 变量，
 * 保证 hover / 选中背景随亮暗主题与 themeOverrides 同步更新。
 */
const themeVars = useThemeVars();

/**
 * 结果行主题色 CSS 变量（供 scoped 样式中的 :hover / --active 使用）。
 *
 * 设计说明：
 * 1. hover 使用 Naive hoverColor（浅灰蒙层）；
 * 2. 选中使用 textColor2 按较高不透明度做 color-mix，保证同为灰色但对比明显，且随亮暗主题自适应。
 */
const panelThemeCssVars = computed<Record<string, string>>(() => ({
  '--measure-result-row-hover-bg': themeVars.value.hoverColor,
  '--measure-result-row-active-bg': `color-mix(in srgb, ${themeVars.value.textColor2} 22%, transparent)`,
  '--measure-result-row-radius': themeVars.value.borderRadius,
}));

const hasContent = computed(() => {
  return props.pairCallouts.length > 0 || props.singleViewState !== null;
});

const singleTitle = computed(() => {
  if (!props.singleViewState) {
    return '';
  }
  return `${props.singleViewState.entityKindLabel} · ${props.singleViewState.entityName}`;
});

/**
 * 判断结果项是否处于选中高亮态。
 *
 * @param kind 结果项种类。
 * @returns 是否高亮。
 */
function isItemActive(kind: MeasurePairMeasurementCalloutKind): boolean {
  return isMeasureResultItemActive(kind, props.lineVisibilityGroup);
}
</script>

<style scoped lang="less">
.measure-result-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--n-border-color);
  max-height: min(42vh, 320px);
  overflow: auto;
}

.measure-result-panel__empty {
  padding: 8px 4px;
  font-size: 12px;
}

.measure-result-panel__section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.measure-result-panel__section-title {
  padding: 2px 4px 4px;
}

.measure-result-panel__entity-title {
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.measure-result-row {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 8px;
  align-items: start;
  padding: 6px 8px;
  border: none;
  border-radius: var(--measure-result-row-radius, 3px);
  background: transparent;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  &--selectable {
    cursor: pointer;

    &:hover:not(.measure-result-row--active) {
      background: var(--measure-result-row-hover-bg);
    }
  }

  &--active {
    background: var(--measure-result-row-active-bg);
  }

  &__label {
    font-size: 12px;
    line-height: 18px;
  }

  &__value {
    font-size: 12px;
    line-height: 18px;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
}
</style>
